/**
 * Analytics and GPS events on the timeline: per lane, next/previous,
 * and which count as of interest (design §8.4).
 *
 * @module
 */
import type { TimelineEvent } from "../api/client";

/** Events belong to the lane of their file's library and source. */
export const laneKey = (libraryId: string, source: string | null | undefined) =>
  `${libraryId}|${source ?? ""}`;

export function byLane(
  events: readonly TimelineEvent[],
): Map<string, TimelineEvent[]> {
  const lanes = new Map<string, TimelineEvent[]>();
  for (const e of events) {
    const key = laneKey(e.library_id, e.source);
    lanes.set(key, [...(lanes.get(key) ?? []), e]);
  }
  return lanes;
}

/** Within this of the playhead counts as being at the event, so Next moves on. */
const AT_MS = 500;

/** The first event starting after `t`, by start time. */
export function nextEvent(
  events: readonly TimelineEvent[],
  t: number,
): TimelineEvent | undefined {
  return events.find((e) => Date.parse(e.start_utc) > t + AT_MS);
}

/** The last event starting before `t`. */
export function previousEvent(
  events: readonly TimelineEvent[],
  t: number,
): TimelineEvent | undefined {
  return events.findLast((e) => Date.parse(e.start_utc) < t - AT_MS);
}

/**
 * Context rather than events: the camera moving (body-worn and vehicle
 * cameras move most of the time) and GPS moving or stationary. They are
 * drawn on the lanes and used in combined queries, not stepped to.
 */
const CONTEXT = new Set(["camera_moving", "moving", "stationary"]);

/** Events worth stepping to and listing. */
export const isOfInterest = (e: TimelineEvent) => !CONTEXT.has(e.kind);

/**
 * A transcribed recording's speech as what was said: its `said` events
 * stand in for its `speech` ones, so each stretch is listed once.
 */
export function withWords(events: readonly TimelineEvent[]): TimelineEvent[] {
  const transcribed = new Set(
    events.filter((e) => e.kind === "said").map((e) => e.file_id),
  );
  return events.filter(
    (e) => !(e.kind === "speech" && transcribed.has(e.file_id)),
  );
}

export const KINDS: Record<string, string> = {
  speech: "Speech",
  said: "Speech with words",
  motion: "Motion",
  loud: "Loud event",
  moving: "Moving (GPS)",
  stationary: "Stationary (GPS)",
  camera_moving: "Camera moving",
};

/** Events of one kind, from one source or (null) any. */
export interface Term {
  kind: string;
  source: string | null;
}

export type Relation = "while" | "while_not";

/** Part of a `find` event, in UTC milliseconds. */
export interface Match {
  from: number;
  to: number;
  event: TimelineEvent;
}

const matching = (events: readonly TimelineEvent[], t: Term) =>
  events.filter(
    (e) => e.kind === t.kind && (t.source === null || e.source === t.source),
  );

/** The times covered by events matching `t`, merged and in order. */
function spans(events: readonly TimelineEvent[], t: Term): [number, number][] {
  const out: [number, number][] = [];
  const sorted = matching(events, t)
    .map((e): [number, number] => [
      Date.parse(e.start_utc),
      Date.parse(e.end_utc),
    ])
    .sort((a, b) => a[0] - b[0]);
  for (const [s, e] of sorted) {
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

/**
 * When `find` happens while `during` does, or doesn't (design §8.4): for
 * example motion on camera 1 while VAN7 is stationary.
 */
export function timesOfInterest(
  events: readonly TimelineEvent[],
  find: Term,
  relation: Relation,
  during: Term,
): Match[] {
  const cover = spans(events, during);
  const out: Match[] = [];
  for (const event of matching(events, find)) {
    const from = Date.parse(event.start_utc);
    const to = Date.parse(event.end_utc);
    if (relation === "while") {
      for (const [s, e] of cover) {
        const lo = Math.max(from, s);
        const hi = Math.min(to, e);
        if (lo < hi) out.push({ from: lo, to: hi, event });
      }
      continue;
    }
    let cursor = from;
    for (const [s, e] of cover) {
      if (e <= cursor) continue;
      if (s >= to) break;
      if (s > cursor) out.push({ from: cursor, to: s, event });
      cursor = Math.max(cursor, e);
    }
    if (cursor < to) out.push({ from: cursor, to, event });
  }
  return out.sort((a, b) => a.from - b.from);
}

export function describe(e: TimelineEvent): string {
  if (e.kind === "loud") {
    return `Loud event${e.level_dbfs != null ? ` (${Math.round(e.level_dbfs)} dBFS)` : ""}`;
  }
  const s = Math.round(
    (Date.parse(e.end_utc) - Date.parse(e.start_utc)) / 1000,
  );
  const length =
    s < 1
      ? "under 1 s"
      : s < 60
        ? `${s} s`
        : `${Math.floor(s / 60)} min ${s % 60} s`;
  if (e.kind === "motion") {
    return `Motion, ${length}${e.peak != null ? ` (up to ${Math.max(1, Math.round(e.peak * 100))}% of the picture)` : ""}`;
  }
  if (e.kind === "camera_moving") return `Camera moving, ${length}`;
  if (e.kind === "moving") return `Moving, ${length}`;
  if (e.kind === "stationary") return `Stationary, ${length}`;
  if (e.kind === "said") {
    if (!e.text) return `Speech, ${length}`;
    const who = e.speaker != null ? `Speaker ${e.speaker}: ` : "";
    return e.translation
      ? `${who}“${e.text}” (in English: “${e.translation}”)`
      : `${who}“${e.text}”`;
  }
  return `Speech, ${length}`;
}
